import type { ViewQueryList, ViewQueryNode } from '@salescore/core'
import { message } from 'antd'
import { Result } from 'effect'
import { t } from 'i18next'
import { useEffect, useState } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column.ts'
import { flattenCopyMatrix } from '../../../domain/copyAndPaste/pasteTargetStructure/flattenCopyMatrix.ts'
import { useCursorMutation } from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { computeChildInsertInnerRowIndex } from '../../util/computeInsertPosition'
import type { ClipboardReadSuccess, UsePasteAlreadyReadClipboardArgument } from './usePaste'
import { clipboardToCopyMatrix, readFromClipboard, usePaste } from './usePaste'

// 追加すべきレコード数は「コピー元の親/ルート行数」ではなく、ペースト側が実際に展開する平坦化後の内部行数。
// rebuildCopyMatrixForPasteTarget も同じ flattenCopyMatrix で totalRows を決めるため、追加数とペースト行数が一致する。
// 単一親配下の複数子行コピー (子列のみ) では cellGroup に N 件入り rows.length=1 となるが、平坦化後は N 行になる。
const getNumberOfPseudoRowsToCreate = (clipboard: ClipboardReadSuccess): number =>
  flattenCopyMatrix(clipboardToCopyMatrix(clipboard)).rows.length

interface PasteWithNewRecordArgument {
  rowIndex: number
  innerRowIndex: number
  node: ViewQueryNode
}

//
// 「レコードを追加して貼り付け」のためのhooks
// 現在clipboardにあるテキストを読み取り、その行数分のレコードを追加してから、追加した行に対して貼り付けを行う。

export function usePasteWithNewRecord(
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
): (argument: PasteWithNewRecordArgument) => Promise<void> {
  const { addRootRecords, addChildRecords } = useAddEmptyRecordMutation(query, ability)
  const records = useRecordsImpl()
  const paste = usePaste(columns, ability, query)
  const [pendingPaste, setPendingPaste] = useState<UsePasteAlreadyReadClipboardArgument | undefined>(undefined)
  const { hideContextMenu, moveToRowIndex, moveToInnerRowIndex } = useCursorMutation(ability)

  const pasteWithNewRecord = async ({ rowIndex, innerRowIndex, node }: PasteWithNewRecordArgument): Promise<void> => {
    const clipboard = await readFromClipboard()
    if (!clipboard.success) {
      if (clipboard.error instanceof Error) {
        void message.error(`${t(`クリップボードからの読み取りに失敗しました。`)}${clipboard.error.message}`)
      } else {
        void message.error(t(`クリップボードからの読み取りに失敗しました。`))
      }
      return
    }
    const pseudoRowsToCreate = getNumberOfPseudoRowsToCreate(clipboard)
    const isRoot = node.path.length <= 1
    if (isRoot) {
      const insertedRowIndex = await addRootRecords({ rowIndex, num: pseudoRowsToCreate })
      if (insertedRowIndex === undefined) {
        return
      }
      moveToRowIndex(insertedRowIndex)
    } else {
      // 追加適用前の records を使って挿入起点を決め、producer 側の内部計算と同じ座標へカーソルを移す。
      const baseResult = (() => {
        const rootRecordNode = records[rowIndex]
        return rootRecordNode === undefined
          ? Result.fail({ message: 'ルートレコードが存在しません' })
          : computeChildInsertInnerRowIndex(rootRecordNode, innerRowIndex, node)
      })()
      await addChildRecords({
        rowIndex,
        innerRowIndex,
        node,
        num: pseudoRowsToCreate,
        additionalFieldValues: [],
      })
      if (Result.isFailure(baseResult)) {
        return
      }
      moveToInnerRowIndex(baseResult.success)
    }
    setPendingPaste({
      type: 'alreadyReadClipboard',
      clipboard,
    })
  }

  useEffect(() => {
    if (pendingPaste !== undefined) {
      // 新規レコード追加後の反映を待ってからペーストする。
      hideContextMenu()
      void paste(pendingPaste).finally(() => {
        void message.info(t(`ペーストが完了しました`))
        setPendingPaste(undefined)
      })
    }
  }, [hideContextMenu, paste, pendingPaste])

  return pasteWithNewRecord
}
